import type { IForecasts } from "~/stores/sku-store"
import { api, type RouterInputs, type RouterOutputs } from "~/utils/api"
import {
  runPartialRefresh,
  runPartialRefreshBatch,
  type PartialRefreshOps,
} from "~/utils/cacheUtils/runPartialRefresh"
import { variantsRefreshOps } from "~/utils/cacheUtils/variantCacheHelpers"

/** Optimized weights format: Record<variantId, Record<skuId, weight>> */
type IWeightsOptimized = Record<string, Record<string, number>>

/**
 * Merges new forecast data into existing React Query cache without losing unrelated data.
 * Uses warehouse-level maps to efficiently update only changed variants while preserving others.
 *
 * @param existingData - Current cached forecast data (undefined if cache is empty)
 * @param updatedData - New forecast data from server mutation response
 * @param scopedVariantIds - Refreshed variantIds; any absent from `updatedData` for a
 *   warehouse is evicted (uncap/adjustment-delete dropped it), not left stale.
 * @returns Merged forecast data array, or empty array if inputs are invalid
 *
 * @example
 * // In a mutation's onSuccess handler:
 * utils.forecasts.getAllForecasts.setData(undefined, (oldData) => {
 *   return mergeForecastsIntoCache(oldData, data.forecasts, data.variantIds)
 * })
 */
export function mergeForecastsIntoCache(
  existingData: IForecasts[] | undefined,
  updatedData: IForecasts[] | undefined,
  scopedVariantIds?: string[],
): IForecasts[] {
  if (!existingData) {
    return Array.isArray(updatedData) ? updatedData : []
  }

  const updates = updatedData ?? []
  // Empty slice + scope = all scoped variants vanished; fall through to eviction.
  if (!updates.length && !scopedVariantIds?.length) {
    return existingData
  }

  const warehouseMap: Record<string, IForecasts> = {}

  for (const warehouse of existingData) {
    warehouseMap[warehouse.warehouseId] = {
      warehouseId: warehouse.warehouseId,
      forecasts: { ...warehouse.forecasts },
      model: { ...warehouse.model },
      specialSkuForecasts: { ...warehouse.specialSkuForecasts },
    }
  }

  const updatedByWarehouse: Record<string, IForecasts> = {}
  for (const updatedWarehouse of updates) {
    updatedByWarehouse[updatedWarehouse.warehouseId] = updatedWarehouse
    const existing = warehouseMap[updatedWarehouse.warehouseId]

    if (existing) {
      for (const variantId of Object.keys(updatedWarehouse.forecasts)) {
        existing.forecasts[variantId] = updatedWarehouse.forecasts[variantId]!
      }
      for (const variantId of Object.keys(updatedWarehouse.model)) {
        existing.model[variantId] = updatedWarehouse.model[variantId]!
      }
      for (const skuId of Object.keys(updatedWarehouse.specialSkuForecasts)) {
        existing.specialSkuForecasts[skuId] = updatedWarehouse.specialSkuForecasts[skuId]!
      }
    } else {
      warehouseMap[updatedWarehouse.warehouseId] = {
        warehouseId: updatedWarehouse.warehouseId,
        forecasts: { ...updatedWarehouse.forecasts },
        model: { ...updatedWarehouse.model },
        specialSkuForecasts: { ...updatedWarehouse.specialSkuForecasts },
      }
    }
  }

  // Evict scoped variants absent from the returned slice — they lost their forecast.
  if (scopedVariantIds?.length) {
    for (const warehouse of Object.values(warehouseMap)) {
      const returned = updatedByWarehouse[warehouse.warehouseId]
      for (const variantId of scopedVariantIds) {
        if (!returned || !(variantId in returned.forecasts)) {
          delete warehouse.forecasts[variantId]
          delete warehouse.model[variantId]
        }
      }
    }
  }

  return Object.values(warehouseMap)
}

/**
 * Merges new weights data into existing cache.
 *
 * @param existing - Current cached weights (Record<variantId, Record<skuId, weight>>)
 * @param updated - New weights from server
 * @returns Merged weights object
 */
function mergeWeightsIntoCache(
  existing: IWeightsOptimized | undefined,
  updated: IWeightsOptimized | undefined,
): IWeightsOptimized {
  const merged = { ...existing }
  for (const [variantId, skuWeights] of Object.entries(updated ?? {})) {
    merged[variantId] = { ...merged[variantId], ...skuWeights }
  }
  return merged
}

/**
 * Updates the forecast cache with fresh data for specific variants.
 * Implements the cache refresh pattern with proper error handling and race condition prevention.
 */
function forecastsRefreshOps(
  utils: ReturnType<typeof api.useUtils>,
  variantIds: string[],
): PartialRefreshOps<RouterOutputs["forecasts"]["getForecastsForVariants"]> {
  return {
    cancel: () => {
      void utils.forecasts.getAllForecasts.cancel()
    },
    fetch: () => utils.forecasts.getForecastsForVariants.fetch({ variantIds }, { staleTime: 0 }),
    apply: (updated) => {
      utils.forecasts.getAllForecasts.setData(undefined, (oldData) => {
        if (!oldData) return oldData

        const mergedForecasts = mergeForecastsIntoCache(
          oldData.forecasts,
          updated.forecasts,
          variantIds,
        )
        const mergedWeights = mergeWeightsIntoCache(oldData.weights, updated.weights)

        if (!mergedForecasts || mergedForecasts.length === 0) {
          console.warn("Cache merge returned empty data, keeping old data")
          return oldData
        }

        return { forecasts: mergedForecasts, weights: mergedWeights }
      })
    },
    fallback: () => {
      void utils.forecasts.getAllForecasts.invalidate()
    },
  }
}

async function refreshForecastsCache(utils: ReturnType<typeof api.useUtils>, variantIds: string[]) {
  if (!variantIds || variantIds.length === 0) return
  await runPartialRefresh(forecastsRefreshOps(utils, variantIds))
}

/**
 * Hook for partial forecast cache refresh.
 * Encapsulates global state dependencies (utils) for cleaner usage in mutations.
 *
 * @returns Function that accepts variantIds and refreshes their forecasts in cache
 *
 * @example
 * const refreshForecasts = usePartialRefreshForecasts()
 * // In mutation onSuccess:
 * await refreshForecasts(data.variantIds)
 */
export function usePartialRefreshForecasts() {
  const utils = api.useUtils()

  return async (variantIds: string[]) => {
    await refreshForecastsCache(utils, variantIds)
  }
}

// Forecasts-router queries a scoped edit's router-wide invalidate skips: getAllForecasts is
// partial-merged instead (refetching it org-wide is expensive), and the historic snapshots
// are written only by the nightly load, so an edit can't change them. A broad refresh (clear
// all, data load) invalidates the whole router.
const NOT_REFRESHED_ON_EDIT: ReadonlySet<string> = new Set<keyof RouterInputs["forecasts"]>([
  "getAllForecasts",
  "getHistoricForecast",
  "getHistoricForecastRanges",
  "getHistoricForecastDates",
  "getHistoricForecastsByWeekDiff",
  "getHistoricForecastDatesForVariants",
  "getHistoricForecastOnDate",
])

/**
 * Refreshes every cache that reads live forecast data. Required entry point for
 * any mutation that edits forecasts (adjustments, monthly adjust, uncap, NPI
 * activation, demand-plan publish) — never hand-roll forecast invalidation lists.
 *
 * With `variantIds`, the merge-backed caches (getAllForecasts,
 * variantTableData.variants) are partial-merged; without, they are fully
 * invalidated (for global ops like clear-all-monthly-adjust). Stock-out times
 * (stockOutTimesV2) are refreshed out-of-band by the ForecastedReturns update
 * event, since they depend on the async returns recompute; the full-invalidate
 * path still clears them directly for global ops. The
 * `forecasts` router is invalidated wholesale so newly added forecast queries
 * are covered automatically; forecast-reading queries OUTSIDE that router must
 * be added here.
 */
export async function refreshForecastDomainCache(
  utils: ReturnType<typeof api.useUtils>,
  variantIds?: string[],
): Promise<void> {
  // Batch the merges so their setData calls land in one render, not staggered ones.
  const partialIds = variantIds && variantIds.length > 0 ? variantIds : null
  const coreRefresh = partialIds
    ? runPartialRefreshBatch([
        forecastsRefreshOps(utils, partialIds),
        variantsRefreshOps(utils, partialIds),
      ])
    : Promise.all([
        utils.forecasts.getAllForecasts.invalidate(),
        utils.variantTableData.variants.invalidate(),
        utils.productTableData.stockOutTimesV2.invalidate(),
      ])

  await Promise.all([
    coreRefresh,
    utils.forecasts.invalidate(undefined, {
      predicate: (q) =>
        !partialIds ||
        !NOT_REFRESHED_ON_EDIT.has((q.queryKey[0] as string[] | undefined)?.[1] ?? ""),
    }),
    utils.product.salesAndForecasts.invalidate(),
    utils.adjustment.invalidate(),
    // NPI list's demand pair. Both re-run buildForecast off live base data, so they go stale on
    // any forecast edit — and on activation in particular, where the variant only gains a
    // forecast once Sku.activatedFrom is written. Named individually rather than invalidating
    // the npi router, which also holds the expensive variants query.
    utils.npi.npiDemandTotals.invalidate(),
    utils.npi.simulateNewForecast.invalidate(),
  ])
}

export function useRefreshForecastDomain() {
  const utils = api.useUtils()
  return (variantIds?: string[]) => refreshForecastDomainCache(utils, variantIds)
}
